/* Essencraft governed storefront analytics contract.
 *
 * Boundaries:
 * - Shopify owns commerce events and Recharge owns subscription state;
 * - this bridge publishes privacy-safe behavioral events only;
 * - Shopify Customer Events applies consent before pixels receive an event;
 * - no names, contact data, account data, free-form customer text or secrets;
 * - unknown events and properties are discarded to prevent analytics drift.
 */
(function () {
  'use strict';

  var EVENT_VERSION = '1.0.0';

  var ALLOWED_EVENTS = {
    ec_page_variant_viewed: true,
    ec_pdp_view: true,
    ec_section_viewed: true,
    ec_proof_opened: true,
    ec_review_theme_selected: true,
    ec_coa_open: true,
    ec_final_cta_click: true,
    ec_faq_open: true,
    ec_format_compare_click: true,
    ec_support_escalation_click: true,
    ec_motion_step_view: true,
    ec_media_view: true,
    ec_purchase_mode_selected: true,
    ec_subscription_cadence_selected: true,
    ec_add_to_cart_attempt: true,
    ec_popup_impression: true,
    ec_popup_dismiss: true,
    ec_popup_back: true,
    ec_popup_goal_selected: true,
    ec_popup_recommendation_view: true,
    ec_popup_offer_click: true,
    ec_popup_email_capture_started: true,
    ec_popup_email_submit: true,
    ec_popup_email_fallback_ready: true,
    ec_popup_email_error: true,
    ec_popup_sms_skip: true,
    ec_popup_sms_submit: true,
    ec_popup_sms_error: true,
    ec_popup_capture_retry: true,
    ec_popup_handoff_ready: true,
    ec_popup_handoff_error: true
  };

  var ALLOWED_PROPERTIES = {
    product_id: true,
    product_handle: true,
    variant_id: true,
    selling_plan_id: true,
    page_variant_id: true,
    section_id: true,
    section_type: true,
    faq_id: true,
    media_slot: true,
    step_index: true,
    purchase_mode: true,
    cadence: true,
    quantity: true,
    value: true,
    currency: true,
    source: true,
    route: true,
    campaign: true,
    version: true,
    experiment_id: true,
    experiment_version: true,
    experiment_variant: true,
    goal: true,
    concern: true,
    recommendation: true,
    reason: true,
    step: true,
    status: true,
    provider: true,
    channel: true,
    attempts: true,
    commerce_verified: true,
    plan_selected: true,
    plan_requested: true,
    offer: true,
    device: true,
    from_step: true,
    to_step: true,
    session_impression: true,
    profile_status: true,
    capture_path: true,
    error_category: true
  };

  function safeToken(value) {
    if (typeof value !== 'string') return '';
    return value.slice(0, 120).replace(/[^a-zA-Z0-9_.:/-]/g, '_');
  }

  function safeValue(value) {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') return safeToken(value);
    return undefined;
  }

  function cleanProperties(payload) {
    var result = {};
    if (!payload || typeof payload !== 'object') return result;
    Object.keys(ALLOWED_PROPERTIES).forEach(function (key) {
      if (!Object.prototype.hasOwnProperty.call(payload, key)) return;
      var value = safeValue(payload[key]);
      if (value !== undefined && value !== '') result[key] = value;
    });
    return result;
  }

  function campaignContext() {
    var result = {};
    try {
      var params = new URL(window.location.href).searchParams;
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(function (key) {
        var value = safeToken(params.get(key) || '');
        if (value) result[key] = value;
      });
    } catch (error) { /* campaign context is optional */ }
    return result;
  }

  function baseContext() {
    var body = document.body;
    return Object.assign({
      event_version: body && body.getAttribute('data-event-version') || EVENT_VERSION,
      event_timestamp: new Date().toISOString(),
      product_id: body && body.getAttribute('data-product-id') || '',
      product_handle: body && body.getAttribute('data-product-handle') || '',
      page_variant_id: body && body.getAttribute('data-page-variant-id') || 'canonical',
      pathname: window.location.pathname
    }, campaignContext());
  }

  function publishToShopify(eventName, properties) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(eventName, properties);
      }
    } catch (error) { /* analytics must never block the storefront */ }
  }

  function track(eventName, payload) {
    if (!ALLOWED_EVENTS[eventName]) return false;
    var data = Object.assign(baseContext(), cleanProperties(payload));

    try {
      document.dispatchEvent(new CustomEvent(eventName, { detail: data, bubbles: true }));
    } catch (error) { /* older browsers may not support CustomEvent construction */ }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, data));
    publishToShopify(eventName, data);
    return true;
  }

  window.ecAnalyticsContract = {
    version: EVENT_VERSION,
    events: Object.keys(ALLOWED_EVENTS),
    track: track
  };
  window.ecTrack = track;

  function bindRechargeTracking() {
    var widget = document.querySelector('recharge-subscription-widget');
    var widgetRoot = widget && widget.shadowRoot;
    if (!widgetRoot) return false;

    if (!widgetRoot.__ecPurchaseModeTracking) {
      widgetRoot.__ecPurchaseModeTracking = true;
      widgetRoot.addEventListener('change', function (event) {
        var input = event.target;
        if (!input || input.name !== 'purchaseOption' || !input.checked) return;
        track('ec_purchase_mode_selected', {
          purchase_mode: input.value === 'subscription' ? 'subscription' : 'one_time',
          source: 'recharge_widget'
        });
      });
    }

    var plans = widgetRoot.querySelector('rc-selling-plans');
    var plansRoot = plans && plans.shadowRoot;
    if (!plansRoot) return false;

    if (!plansRoot.__ecCadenceTracking) {
      plansRoot.__ecCadenceTracking = true;
      plansRoot.addEventListener('change', function (event) {
        var input = event.target;
        if (!input || input.name !== 'rc_plan' || !input.checked) return;
        track('ec_subscription_cadence_selected', {
          selling_plan_id: input.value,
          cadence: input.getAttribute('aria-label') || '',
          source: 'recharge_widget'
        });
      });
    }

    return true;
  }

  function initRechargeTracking() {
    var attempts = 0;
    var timer = window.setInterval(function () {
      attempts += 1;
      if (bindRechargeTracking() || attempts >= 80) window.clearInterval(timer);
    }, 250);
  }

  function initialView() {
    track('ec_page_variant_viewed', {});
    if (document.body && document.body.getAttribute('li-page') === 'product') {
      track('ec_pdp_view', {});
      initRechargeTracking();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialView);
  } else {
    initialView();
  }
})();
